// VIB-584: Match parsed PDF-tilbudslinjer mot offer_line_templates.
//
// Når en parset linje matcher en mal-rad, bruker vi mal-radens phase,
// cost_type og pricing_type i stedet for parserens heuristikk. Det
// betyr f.eks. at "Regissør 4d kr 32 000" alltid havner i Produksjon-
// fasen, uansett hvor i PDF-en linja sto, fordi mal-radens phase er
// "production".
//
// Linjer som ikke matcher noen mal beholder sin parsede phase, og UI
// kan flagge dem ("Ingen mal-match — sjekk fase manuelt").

import type { OfferLineTemplate } from "@/hooks/use-offers";
import type { BudgetLineExtracted, ProjectPhase } from "./offer-types";

export interface TemplateMatch {
  template_id: string;
  template_name: string;
  phase: ProjectPhase;
  cost_type: string;
  pricing_type: string;
  default_unit: string;
}

export interface MatchedBudgetLine extends BudgetLineExtracted {
  template_match: TemplateMatch | null;
  /** Effektiv phase: template-match overstyrer parser-detected phase */
  effective_phase: ProjectPhase;
  /** Effektiv cost_type: template-match overstyrer "internal hvis has_time" */
  effective_cost_type: "internal" | "external";
  /**
   * VIB-1021: tilvalg-flagg stemplet fra kildebudsjettet ved kombinering av
   * flere budsjetter (OfferPicker). Undefined for ukombinerte budsjetter, der
   * tilvalg-status leses fra budsjett-nivå (ExtractedBudget.is_optional).
   */
  is_optional?: boolean;
  /**
   * VIB-1142: navnet på kilde-budsjettet en tilvalg-linje kom fra, stemplet ved
   * kombinering i OfferPicker. Brukes av import-hooken til å lage én seksjon per
   * tilvalg-budsjett. null = hovedbudsjett-linje; undefined for ukombinerte.
   */
  option_group?: string | null;
}

/** Trim/lowercase for sammenligning. Bevares for diagnostikk i UI. */
function normalize(s: string): string {
  return s.trim().toLowerCase();
}

/** Splitter på " - " og fjerner felles prefikser som "Utstyrsleie". */
function stripCommonPrefix(desc: string): string {
  // Hvis description er "X - Y - Z", behold siste del som hovedoppgave
  const parts = desc.split(" - ").map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return desc;
  return parts[parts.length - 1];
}

/** Tokeniser til ord-set, fjerner punktuasjon og småord. */
const STOPWORDS = new Set(["av", "og", "for", "i", "på", "med", "til", "fra", "om", "en", "et"]);
function tokenize(s: string): Set<string> {
  return new Set(
    s
      .toLowerCase()
      .replace(/[^\wæøåéèüöä\s/]/g, " ")
      .split(/\s+/)
      .filter((w) => w.length > 2 && !STOPWORDS.has(w)),
  );
}

/** Token-overlap som andel av minste set — robust mot ekstra ord */
function tokenOverlapScore(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersect = 0;
  for (const t of a) if (b.has(t)) intersect++;
  return intersect / Math.min(a.size, b.size);
}

/**
 * Synonym-mapping for vanlige avvik mellom tilbuds-PDF-tekst og
 * offer_line_templates. Nøkkelen er en hint som finnes i description,
 * verdien er template-navnet (case-insensitiv match).
 */
const SYNONYMS: Array<[RegExp, string]> = [
  // Utstyrsleie + RED/Sony/Canon/etc → Kamerapakke
  [/\b(red|sony|canon|kamera|alexa|komodo)\b/i, "kamerapakke"],
  // Drone-utstyr
  [/\bdrone\b/i, "drone"],
  // Lyd-relaterte
  [/\bboksmusikk\b/i, "musikk"],
  [/\bvoice\s*over\b/i, "voiceover"],
  // Klipp-varianter
  [/\bklipp.*(grade|color)\b/i, "klipp/redigering"],
  [/\bgrade.*color/i, "grade/colorgrade"],
  // Regi/foto prep
  [/\bforberedelse.*(regi|foto)\b/i, "regi/foto prep"],
];

interface IndexEntry {
  template: OfferLineTemplate;
  nameLower: string;
  tokens: Set<string>;
}

function buildTemplateIndex(templates: OfferLineTemplate[]): IndexEntry[] {
  const seen = new Set<string>();
  const index: IndexEntry[] = [];
  for (const t of templates) {
    if (!t.is_active) continue;
    const key = normalize(t.name);
    if (seen.has(key)) continue; // dedup på navn på tvers av maler
    seen.add(key);
    index.push({
      template: t,
      nameLower: key,
      tokens: tokenize(t.name),
    });
  }
  return index;
}

/**
 * Match én PDF-linje mot mal-listen. Strategi (i prioritert rekkefølge):
 *   1. Eksakt match på lowercased description (eller stripped variant)
 *   2. Substring match begge retninger — lengste mal-navn vinner
 *   3. Synonym-regex som mapper kjente termer til mal-navn
 *   4. Token-overlap >= 50 % — ord-baserte fuzzy-treff
 */
export function matchTemplateForLine(
  description: string,
  templates: OfferLineTemplate[],
): OfferLineTemplate | null {
  if (!description || templates.length === 0) return null;
  const desc = normalize(description);
  const stripped = normalize(stripCommonPrefix(description));
  const index = buildTemplateIndex(templates);
  const indexByName = new Map(index.map((e) => [e.nameLower, e.template]));

  // 1. Eksakt match (full eller stripped)
  if (indexByName.has(desc)) return indexByName.get(desc)!;
  if (stripped !== desc && indexByName.has(stripped)) return indexByName.get(stripped)!;

  // 2. Substring i begge retninger. Velg lengste mal-navn.
  let bestSubstr: OfferLineTemplate | null = null;
  let bestSubstrLen = 0;
  for (const e of index) {
    if (desc.includes(e.nameLower) || e.nameLower.includes(desc) ||
        stripped.includes(e.nameLower) || e.nameLower.includes(stripped)) {
      if (e.nameLower.length > bestSubstrLen) {
        bestSubstr = e.template;
        bestSubstrLen = e.nameLower.length;
      }
    }
  }
  if (bestSubstr) return bestSubstr;

  // 3. Synonym-regex
  for (const [pattern, malHint] of SYNONYMS) {
    if (pattern.test(description)) {
      const hit = index.find((e) => e.nameLower.includes(malHint.toLowerCase()));
      if (hit) return hit.template;
    }
  }

  // 4. Token-overlap >= 50 %. Velg høyeste score.
  const descTokens = tokenize(desc);
  let bestToken: OfferLineTemplate | null = null;
  let bestScore = 0.49; // krever > 50 %
  for (const e of index) {
    const score = tokenOverlapScore(descTokens, e.tokens);
    if (score > bestScore) {
      bestToken = e.template;
      bestScore = score;
    }
  }
  return bestToken;
}

/** Convert mal cost_type til union-type. Mal-rader bruker fri tekst. */
function normalizeCostType(raw: string): "internal" | "external" {
  return raw === "external" ? "external" : "internal";
}

/**
 * Berik PDF-parsede linjer med template-match + effektive felt. UI-en
 * viser disse feltene direkte og kan markere lines hvor template_match
 * er null.
 */
export function enrichBudgetLines(
  lines: BudgetLineExtracted[],
  templates: OfferLineTemplate[],
): MatchedBudgetLine[] {
  return lines.map((line) => {
    const tpl = matchTemplateForLine(line.description, templates);
    if (!tpl) {
      return {
        ...line,
        template_match: null,
        effective_phase: line.phase,
        // Beholder eksisterende heuristikk: timetypede linjer er internal,
        // alt annet external.
        effective_cost_type: line.has_time ? "internal" : "external",
      };
    }
    const match: TemplateMatch = {
      template_id: tpl.id,
      template_name: tpl.name,
      phase: tpl.phase as ProjectPhase,
      cost_type: tpl.cost_type,
      pricing_type: tpl.pricing_type,
      default_unit: tpl.default_unit,
    };
    return {
      ...line,
      template_match: match,
      // Mal-radens phase overstyrer parser-detected phase. Det betyr at
      // linjer som var feilplassert pga manglende fase-header havner i
      // riktig fase basert på malen.
      effective_phase: match.phase,
      effective_cost_type: normalizeCostType(match.cost_type),
    };
  });
}
